/* eslint-disable no-unused-vars */

import _ from 'lodash';
import moment from 'moment';
import { getLatestCoordinates } from './get-distance';
import { getAppDispatch } from './dispatch.util';
import { cacheActions } from '@redux/slices/cache';

const dueDateFilter = ({ taskDueDate, dueDateFilter }) => {
  const currentDate = moment().format('YYYY-MM-DD');

  const taskDueDateWithoutTimeZone = moment(taskDueDate).format('YYYY-MM-DD');
  if (dueDateFilter?.selected === 'next') {
    if (dueDateFilter.value?.includes('Day')) {
      const nextDay = moment().add(1, 'd').format('YYYY-MM-DD');
      if (moment(taskDueDateWithoutTimeZone).isSameOrBefore(nextDay)) return true;
    }
    if (dueDateFilter.value?.includes('Week (7 days)')) {
      const currentDateIncreaseBySevenDay = moment().add('1', 'w').format('YYYY-MM-DD');
      if (moment(taskDueDateWithoutTimeZone).isSameOrBefore(currentDateIncreaseBySevenDay))
        return true;
    }
    if (dueDateFilter.value?.includes('Month (30 days)')) {
      const currentDateIncreaseBy30Day = moment().add('1', 'M').format('YYYY-MM-DD');
      if (moment(taskDueDateWithoutTimeZone).isSameOrBefore(currentDateIncreaseBy30Day))
        return true;
    }
    if (dueDateFilter.value?.includes('Quarter (90 days)')) {
      const currentDateIncreaseBy90Day = moment().add('90', 'd').format('YYYY-MM-DD');
      if (moment(taskDueDateWithoutTimeZone).isSameOrBefore(currentDateIncreaseBy90Day))
        return true;
    }
    if (dueDateFilter.value?.includes('Year (365 days)')) {
      const currentDateIncreaseBy365Day = moment().add('1', 'y').format('YYYY-MM-DD');
      if (moment(taskDueDateWithoutTimeZone).isSameOrBefore(currentDateIncreaseBy365Day))
        return true;
    }
    if (dueDateFilter.value?.includes('Unlimited')) {
      const currentDateIncreaseBy365Day = moment().add('100', 'y').format('YYYY-MM-DD');
      if (
        moment(taskDueDateWithoutTimeZone).isBetween(
          currentDate,
          currentDateIncreaseBy365Day,
          undefined,
          '[)'
        )
      )
        return true;
    }

    if (
      dueDateFilter.value?.[0]?.includes('Calendar week') ||
      dueDateFilter.value?.[0]?.includes('Calendar month') ||
      dueDateFilter.value?.[0]?.includes('Calendar year')
    ) {
      const day = parseInt(dueDateFilter.value?.[0]?.match(/\d+/g)[0]);

      const selectedDateStart = moment().add('1', 'd').format('YYYY-MM-DD');
      const selectedDateEnd = moment().add(day.toString(), 'd').format('YYYY-MM-DD');

      if (
        moment(taskDueDateWithoutTimeZone).isBetween(
          selectedDateStart,
          selectedDateEnd,
          undefined,
          '[]'
        )
      )
        return true;
    }
  } else if (dueDateFilter?.selected === 'on') {
    const selectedDate = moment(dueDateFilter.value).format('YYYY-MM-DD');
    if (moment(taskDueDateWithoutTimeZone).isSame(moment(selectedDate))) return true;
  } else if (dueDateFilter?.selected === 'range') {
    const selectedDateStart = moment(dueDateFilter.value[0]).format('YYYY-MM-DD');
    const selectedDateEnd = moment(dueDateFilter.value[1]).format('YYYY-MM-DD');
    if (
      moment(taskDueDateWithoutTimeZone).isBetween(
        selectedDateStart,
        selectedDateEnd,
        undefined,
        '[]'
      )
    )
      return true;
  } else if (dueDateFilter?.selected === 'past') {
    const currentDateDecreaseBy365Day = moment().subtract('100', 'y').format('YYYY-MM-DD');
    if (
      moment(taskDueDateWithoutTimeZone).isBetween(
        currentDateDecreaseBy365Day,
        currentDate,
        undefined,
        '[)'
      )
    )
      return true;
  } else {
    return false;
  }
};

const scheduledDateFilter = ({ startDate, syncedDate, dueDateFilter }) => {
  if (_.isEmpty(startDate) && _.isEmpty(syncedDate)) return false;

  const currentDate = moment().format('YYYY-MM-DD');
  const taskDueDateWithoutTimeZone =
    startDate && moment(startDate).isValid() ? moment(startDate).format('YYYY-MM-DD') : false;
  const syncedDateDateWithoutTimeZone =
    syncedDate && moment(syncedDate).isValid() ? moment(syncedDate).format('YYYY-MM-DD') : false;

  const isDateInRangeOrBefore = (date, start, end) =>
    moment(date).isSameOrBefore(end) && moment(date).isSameOrAfter(start);

  if (dueDateFilter?.selected === 'next') {
    let rangeEnd;
    if (dueDateFilter.value?.includes('Day')) {
      rangeEnd = moment().add(1, 'd').format('YYYY-MM-DD');
    } else if (dueDateFilter.value?.includes('Week (7 days)')) {
      rangeEnd = moment().add(1, 'w').format('YYYY-MM-DD');
    } else if (dueDateFilter.value?.includes('Month (30 days)')) {
      rangeEnd = moment().add(1, 'M').format('YYYY-MM-DD');
    } else if (dueDateFilter.value?.includes('Quarter (90 days)')) {
      rangeEnd = moment().add(90, 'd').format('YYYY-MM-DD');
    } else if (dueDateFilter.value?.includes('Year (365 days)')) {
      rangeEnd = moment().add(1, 'y').format('YYYY-MM-DD');
    } else if (dueDateFilter.value?.includes('Unlimited')) {
      rangeEnd = moment().add(100, 'y').format('YYYY-MM-DD');
    } else if (
      dueDateFilter.value?.[0]?.includes('Calendar week') ||
      dueDateFilter.value?.[0]?.includes('Calendar month') ||
      dueDateFilter.value?.[0]?.includes('Calendar year')
    ) {
      const day = parseInt(dueDateFilter.value?.[0]?.match(/\d+/g)[0]);

      const selectedDateStart = moment().add('1', 'd').format('YYYY-MM-DD');
      const selectedDateEnd = moment().add(day.toString(), 'd').format('YYYY-MM-DD');

      if (
        moment(taskDueDateWithoutTimeZone).isBetween(
          selectedDateStart,
          selectedDateEnd,
          undefined,
          '[]'
        )
      )
        return true;
    }

    if (rangeEnd) {
      // Include tasks that are before or within the range
      if (
        isDateInRangeOrBefore(taskDueDateWithoutTimeZone, '1900-01-01', rangeEnd) ||
        isDateInRangeOrBefore(syncedDateDateWithoutTimeZone, '1900-01-01', rangeEnd)
      ) {
        return true;
      }
    }
  } else if (dueDateFilter?.selected === 'on') {
    const selectedDate = moment(dueDateFilter.value).format('YYYY-MM-DD');

    if (moment(taskDueDateWithoutTimeZone).isSame(moment(selectedDate))) return true;
  } else if (dueDateFilter?.selected === 'range') {
    const selectedDateStart = moment(dueDateFilter.value[0]).format('YYYY-MM-DD');
    const selectedDateEnd = moment(dueDateFilter.value[1]).format('YYYY-MM-DD');
    if (
      moment(taskDueDateWithoutTimeZone).isBetween(
        selectedDateStart,
        selectedDateEnd,
        undefined,
        '[]'
      )
    )
      return true;
  } else if (dueDateFilter?.selected === 'past') {
    const currentDateDecreaseBy365Day = moment().subtract('100', 'y').format('YYYY-MM-DD');

    if (
      moment(taskDueDateWithoutTimeZone).isBetween(
        currentDateDecreaseBy365Day,
        currentDate,
        undefined,
        '[)'
      ) ||
      moment(syncedDateDateWithoutTimeZone).isBetween(
        currentDateDecreaseBy365Day,
        currentDate,
        undefined,
        '[)'
      )
    )
      return true;
  } else {
    return false;
  }
};

const createAtOrSyncedDateFilter = ({ dateForFilter, filterSelectedDate }) => {
  const currentDate = moment().format('YYYY-MM-DD');
  const syncDateCustomWithoutTimeZone = moment(dateForFilter).format('YYYY-MM-DD');
  if (filterSelectedDate?.selected === 'last' && !_.isEmpty(dateForFilter)) return true;

  if (filterSelectedDate?.selected === 'on') {
    const selectedDate = moment(filterSelectedDate?.value).format('YYYY-MM-DD');
    if (moment(syncDateCustomWithoutTimeZone).isSame(moment(selectedDate))) return true;
  }

  if (filterSelectedDate?.selected === 'range') {
    const selectedDateStart = moment(filterSelectedDate?.value[0]).format('YYYY-MM-DD');
    const selectedDateEnd = moment(filterSelectedDate?.value[1]).format('YYYY-MM-DD');
    if (
      moment(selectedDateStart).isSame(moment(selectedDateEnd)) &&
      moment(syncDateCustomWithoutTimeZone).isSame(moment(selectedDateEnd))
    )
      return true;
    else if (
      moment(syncDateCustomWithoutTimeZone).isBetween(
        selectedDateStart,
        selectedDateEnd,
        undefined,
        '[]'
      )
    )
      return true;
  }

  if (filterSelectedDate?.selected === 'past') {
    if (filterSelectedDate?.value?.includes('Day')) {
      const currentDateDecreaseByOneDay = moment().subtract('1', 'd').format('YYYY-MM-DD');
      if (moment(syncDateCustomWithoutTimeZone).isSame(moment(currentDateDecreaseByOneDay)))
        return true;
    }
    if (filterSelectedDate?.value?.includes('Week (7 days)')) {
      const currentDateDecreaseBySevenDay = moment().subtract('1', 'w').format('YYYY-MM-DD');
      if (
        moment(syncDateCustomWithoutTimeZone).isBetween(
          currentDateDecreaseBySevenDay,
          currentDate,
          undefined,
          '[)'
        )
      )
        return true;
    }

    if (filterSelectedDate?.value?.includes('Month (30 days)')) {
      const currentDateDecreaseBy30Day = moment().subtract('1', 'M').format('YYYY-MM-DD');
      if (
        moment(syncDateCustomWithoutTimeZone).isBetween(
          currentDateDecreaseBy30Day,
          currentDate,
          undefined,
          '[)'
        )
      )
        return true;
    }
    if (filterSelectedDate?.value?.includes('Quarter (90 days)')) {
      const currentDateDecreaseBy90Day = moment().subtract('90', 'd').format('YYYY-MM-DD');
      if (
        moment(syncDateCustomWithoutTimeZone).isBetween(
          currentDateDecreaseBy90Day,
          currentDate,
          undefined,
          '[)'
        )
      )
        return true;
    }
    if (filterSelectedDate?.value?.includes('Year (365 days)')) {
      const currentDateDecreaseBy365Day = moment().subtract('1', 'y').format('YYYY-MM-DD');
      if (
        moment(syncDateCustomWithoutTimeZone).isBetween(
          currentDateDecreaseBy365Day,
          currentDate,
          undefined,
          '[)'
        )
      )
        return true;
    }
    if (filterSelectedDate?.value?.includes('Unlimited')) {
      const currentDateDecreaseBy365Day = moment().subtract('100', 'y').format('YYYY-MM-DD');
      if (
        moment(syncDateCustomWithoutTimeZone).isBetween(
          currentDateDecreaseBy365Day,
          currentDate,
          undefined,
          '[)'
        )
      )
        return true;
    }
  } else {
    return false;
  }
};

const isDifferenceGreaterThanTenMinutes = ({ currentDate, prevDate }) => {
  const current = new Date(currentDate);
  const prev = new Date(prevDate);

  const diffInMillis = current - prev;

  const diffInMinutes = diffInMillis / (1000 * 60);

  return diffInMinutes >= 10;
};

const checkIfTheLocationIsNeedToUpdateOrNot = ({
  locationUpdateTime,
  isOnline = false,
  functionCall,
  loadingFunctionCall,
  params,
  oldCoordinates,
  unit,
  addNewCoordinates = false
}) => {
  const dispatch = getAppDispatch();
  const currentDate = moment().format('YYYY-MM-DD HH:mm:ss');

  if (isOnline) {
    if (addNewCoordinates) {
      loadingFunctionCall();
      getLatestCoordinates()
        .then((latestCoordinates) => {
          // const latestCoordinates1 = {
          //   latitude: 39.09955647893262,
          //   longitude: -102.73546874999998
          // };
          dispatch?.(
            cacheActions.setUnitAndCoordinates({
              unit: unit,
              coordinates: latestCoordinates,
              locationUpdateTime: moment().format('MM-DD-YYYY HH:mm:ss')
            })
          );
          dispatch?.(cacheActions.setSitesMobile());
          functionCall({ ...params, coordinates: latestCoordinates, setLatestValue: true });
        })
        .catch((error) => {
          console.log({ error });
          functionCall({ ...params });
        });
    } else {
      if (locationUpdateTime && oldCoordinates) {
        const cond = isDifferenceGreaterThanTenMinutes({
          currentDate: currentDate,
          prevDate: locationUpdateTime
        });
        if (cond) {
          getLatestCoordinates()
            .then((latestCoordinates) => {
              // const latestCoordinates1 = {
              //   latitude: 39.09955647893262,
              //   longitude: -102.73546874999998
              // };

              dispatch?.(
                cacheActions.setUnitAndCoordinates({
                  unit: unit,
                  coordinates: latestCoordinates,
                  locationUpdateTime: moment().format('MM-DD-YYYY HH:mm:ss')
                })
              );
              dispatch?.(cacheActions.setSitesMobile());

              functionCall({ ...params, coordinates: latestCoordinates, setLatestValue: true });
            })
            .catch((error) => {
              console.log({ error });
              functionCall({ ...params });
            });
        } else {
          functionCall({ ...params });
        }
      } else {
        functionCall({ ...params });
      }
    }
  } else {
    functionCall({ ...params });
  }
};

const daysUntilEnd = () => {
  const today = new Date();

  const currentDay = today.getDay(); // 0 (Sunday) to 6 (Saturday)
  const daysToSunday = 7 - currentDay - 1;

  // Calculate days until the end of the month
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const daysToMonthEnd = lastDayOfMonth - today.getDate();

  // Calculate days until the end of the year
  const lastDayOfYear = new Date(today.getFullYear(), 11, 31); // December 31st
  const diffTime = lastDayOfYear - today;
  const daysToYearEnd = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Convert milliseconds to days

  return [
    `Calendar week (${daysToSunday >= 0 ? daysToSunday : 0} days)`,
    `Calendar month (${daysToMonthEnd} days)`,
    `Calendar year (${daysToYearEnd} days)`
  ];
};

const timeToSeconds = ({ time }) => {
  const [hours, minutes, seconds] = time.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

export {
  dueDateFilter,
  scheduledDateFilter,
  createAtOrSyncedDateFilter,
  isDifferenceGreaterThanTenMinutes,
  checkIfTheLocationIsNeedToUpdateOrNot,
  daysUntilEnd,
  timeToSeconds
};
